import { useCallback, useMemo, useState } from "react";
import { type CustomCellEditorProps } from "ag-grid-react";

// A multi-select cell editor for AG Grid Community (ADR 0156/0157). The built-in
// agSelectCellEditor is single-value; this renders a popup of checkboxes so a cell
// can hold a SET of tokens (e.g. a user's tenant roles, or impersonable views).
// The cell value is a string[]; toggling a box relays the new array to the grid via
// onValueChange, so committing the edit fires the grid's normal onCellValueChanged.
// Options (and an optional empty message) arrive through cellEditorParams.

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional group header (e.g. a tenant name when the same role spans tenants). */
  group?: string;
  disabled?: boolean;
}

export interface MultiSelectEditorParams {
  options: MultiSelectOption[];
  emptyText?: string;
}

/** Cell-display helper: turn the token array into a readable label list. */
export function formatMultiSelect(value: unknown, options: MultiSelectOption[], none = "— none —"): string {
  if (!Array.isArray(value) || value.length === 0) return none;
  const label = new Map(options.map((o) => [o.value, o.label] as const));
  return (value as string[]).map((v) => label.get(v) ?? v).join(", ");
}

export function MultiSelectCellEditor(props: CustomCellEditorProps<Record<string, unknown>, string[]>) {
  const params = (props.colDef?.cellEditorParams ?? {}) as Partial<MultiSelectEditorParams>;
  const options = params.options ?? [];
  const emptyText = params.emptyText ?? "No options";
  const [selected, setSelected] = useState<string[]>(() => (Array.isArray(props.value) ? [...props.value] : []));
  // The column sets `cellEditorPopup: true`, so this renders in a popup over the cell.

  const toggle = useCallback(
    (value: string) => {
      const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
      setSelected(next);
      props.onValueChange(next);
    },
    [selected, props],
  );

  const groups = useMemo(() => {
    const m = new Map<string, MultiSelectOption[]>();
    for (const o of options) {
      const g = o.group ?? "";
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(o);
    }
    return [...m.entries()];
  }, [options]);

  return (
    <div role="listbox" aria-multiselectable="true" aria-label="Select options" style={panel}>
      {options.length === 0 ? (
        <div style={{ padding: 10, color: "var(--text-dim)", fontStyle: "italic" }}>{emptyText}</div>
      ) : (
        groups.map(([group, opts]) => (
          <div key={group || "_"}>
            {group && <div style={groupHdr}>{group}</div>}
            {opts.map((o) => (
              <label key={o.value} style={{ ...rowStyle, opacity: o.disabled ? 0.5 : 1 }}>
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  disabled={o.disabled}
                  onChange={() => toggle(o.value)}
                  style={{ accentColor: "#c9a227", margin: 0 }}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        ))
      )}
      <div style={footer}>
        <button type="button" aria-label="Done" onClick={() => props.stopEditing()} style={doneBtn}>
          Done
        </button>
      </div>
    </div>
  );
}

const panel: React.CSSProperties = {
  background: "#1c1d22",
  border: "1px solid rgba(201,162,39,0.35)",
  borderRadius: 6,
  padding: 4,
  minWidth: 180,
  maxHeight: 260,
  overflowY: "auto",
  boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
  fontSize: 13,
  color: "#ece7dd",
};
const groupHdr: React.CSSProperties = {
  padding: "6px 8px 2px",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#a49c90",
};
const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "5px 8px",
  cursor: "pointer",
  borderRadius: 4,
  userSelect: "none",
};
const footer: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  padding: "4px 4px 2px",
  borderTop: "1px solid rgba(255,255,255,0.07)",
  marginTop: 4,
};
const doneBtn: React.CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(201,162,39,0.5)",
  color: "#c9a227",
  borderRadius: 4,
  padding: "3px 12px",
  cursor: "pointer",
  fontSize: 12,
};
